/**
 * @file: useDeliveryCheck.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { AppNavParams } from '@euler/app/Routes';
import { useOrderContext } from '@euler/app/flows/order/functions/OrderContext';
import { TaskNavParams } from '@euler/app/flows/task/TaskFlow';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useDeliveryCheckContext } from '@euler/app/flows/task/functions/task-detail/delivery-check/DeliveryCheckContext';
import { DeliveryCheckManager } from '@euler/app/flows/task/functions/task-detail/delivery-check/DeliveryCheckManager';
import { SamplingCheckManager } from '@euler/app/flows/task/functions/task-detail/delivery-check/SamplingCheckManager';
import { useNavigateToTaskDetail } from '@euler/app/flows/task/functions/useNavigateToTaskDetail';
import { useViewReportWithTaskContext } from '@euler/app/flows/task/functions/useViewReportWithTaskContext';
import { useAppLoading } from '@euler/components/loading/Provider';
import { OrgUserRoleHelper } from '@euler/functions/OrgUserRoleHelper';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { useAppReviewTracking } from '@euler/functions/useAppReviewTracking';
import { useCurrentUserRole } from '@euler/functions/user/useCurrentUserRole';
import { useAlert } from '@euler/functions/ux/useAlert';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { usePrompt } from '@euler/functions/ux/usePrompt';
import { useToggleAutomaticKeyboard } from '@euler/lib/keyboard';
import { HttpError } from '@euler/lib/request';
import {
  CommonTaskStatus,
  DeliveryCheckSceneType,
  InspectionTaskStatus,
  OrgUserRoleType,
  SamplingCheckResultType,
} from '@euler/model/enum';
import { DeliveryCheckItemStagedOption } from '@euler/model/task-detail';
import { ReworkRecordStaged } from '@euler/model/task-detail/ReworkRecord';
import {
  delayed,
  isNotNull,
  safeMarkDevFlag,
  withConfirmation,
} from '@euler/utils';
import {
  useBehaviorSubject,
  useCreation,
  usePersistFn,
} from '@euler/utils/hooks';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { InteractionManager } from 'react-native';
import { Subject } from 'rxjs';
import { useDeliveryCheckCache } from './useDeliveryCheckCache';
import { useDeliveryCheckDirtyCheck } from './useDeliveryCheckDirtyCheck';
import {
  InvalidDeliveryCheckItemHandler,
  validateDeliveryCheckItem,
} from './util';

const kSimulateCommit = safeMarkDevFlag(false);

const kDefaultUnacceptedReasons: string[] = ['已告知客户，客户暂不处理'];

export const useDeliveryCheck = (options: {
  onInvalidItem: InvalidDeliveryCheckItemHandler;
  onNewWorkItems: (items: { key: string; index: number }[]) => void;
}) => {
  const navigation =
    useNavigation<StackNavigationProp<AppNavParams & TaskNavParams>>();
  const trackAppReview = useAppReviewTracking();
  const isMobile = useIsMobileLayout();
  const { onInvalidItem, onNewWorkItems } = options;
  const navigateToTaskDetail = useNavigateToTaskDetail();

  const changed$ = useCreation(() => new Subject<string>(), []);

  useToggleAutomaticKeyboard(true);

  const appLoading = useAppLoading();

  const { rpx } = useResponsiveContext();
  const { taskManager, detail, storeConfiguration, profile, from } =
    useTaskContext();
  const { event$ } = useOrderContext();

  const [errorItemKey, setErrorItemKey] = useState<string>();

  const { manager, timeoutInfo } = useDeliveryCheckContext();
  const [staged] = useBehaviorSubject(manager.staged$);
  const [dirty] = useBehaviorSubject(manager.dirty$);
  const [activeReworkRecord, setActiveReworkRecord] =
    useState<ReworkRecordStaged>();

  useDeliveryCheckCache(manager);
  useDeliveryCheckDirtyCheck(manager);

  const role = useCurrentUserRole();

  const roleHelper = useMemo(() => new OrgUserRoleHelper(role), [role]);

  const expectedRolesForFinish =
    manager.sceneType === DeliveryCheckSceneType.CompletionCheck
      ? storeConfiguration.restrictedRolesForDeliveryCheck?.length
        ? storeConfiguration.restrictedRolesForDeliveryCheck
        : taskManager.currentUser.org.cid === 'bryads'
        ? [OrgUserRoleType.ServiceAgents]
        : []
      : storeConfiguration.restrictedRolesForSamplingDeliveryCheck?.length
      ? storeConfiguration.restrictedRolesForSamplingDeliveryCheck
      : taskManager.currentUser.org.cid === 'bryads'
      ? [OrgUserRoleType.ServiceAgents]
      : [];
  const hasFinishPermission =
    expectedRolesForFinish.length === 0 ||
    roleHelper.hasAnyRole(...expectedRolesForFinish);

  // console.log({ hasFinishPermission });

  const pageTitle =
    profile.store.isIndependentDeliveryCheckEnabled ||
    !detail.basicInfo.isMainOrder
      ? manager.sceneType === DeliveryCheckSceneType.CompletionCheck
        ? '竣工检验'
        : '完工抽检'
      : '完工交车检查';

  const onRemarkTextChange = useCallback(
    (key: string, text: string) => {
      manager.produceItem(key, draft => {
        draft.remark = text;
      });
    },
    [manager],
  );

  const { showActionSheetWithOptions } = useActionSheet();

  const alert = useAlert();
  const prompt = usePrompt();
  const confirm = useConfirm();

  const presentUnacceptedReasonsActionSheet = usePersistFn(
    (callback: (reason: string | undefined) => void) => {
      const reasons = storeConfiguration
        .predefinedDeliveryCheckUnacceptedReasons?.length
        ? storeConfiguration.predefinedDeliveryCheckUnacceptedReasons
        : kDefaultUnacceptedReasons;
      showActionSheetWithOptions(
        {
          title: '未通过检验',
          message: '请选择或手动输入当前项目未通过检验的原因',
          cancelButtonIndex: reasons.length + 1,
          options: [
            ...reasons,
            '手动输入...',
            manager.sceneType === DeliveryCheckSceneType.SamplingCheck
              ? '忽略原因'
              : '取消',
          ],
        },
        async buttonIndex => {
          if (buttonIndex == null || buttonIndex === reasons.length + 1) {
            if (manager.sceneType === DeliveryCheckSceneType.SamplingCheck) {
              callback(undefined);
            }
            return;
          }
          if (buttonIndex === reasons.length) {
            const input = (
              await prompt({
                title: '请输入当前项目未通过检验的原因',
                cancelButtonText: '取消',
                confirmButtonText: '确定',
              })
            )?.trim();
            if (!input) return;
            callback(input);
          } else {
            callback(reasons[buttonIndex]);
          }
        },
      );
    },
  );

  const onViewReworkRecords = usePersistFn(() => {
    navigation.push('_reworkRecords', { sceneType: manager.sceneType });
  });

  const onEditItemReworkRecord = usePersistFn((key: string) => {
    const item = staged.items.find(x => x._key === key);
    if (item == null) return;
    const lastRecord = item.reworkRecords?.[0] as
      | ReworkRecordStaged
      | undefined;
    const record: ReworkRecordStaged = {
      _key: key,
      sceneType: manager.sceneType,
      id: lastRecord?.id,
      licensePlateNo: detail.basicInfo.licensePlateNo,
      vehicleName: detail.basicInfo.vehicleName,
      vehicleBrandName: detail.basicInfo.vehicleBrandName,
      vehicleImageUrl: detail.basicInfo.vehicleImageUrl,
      vehicleModel: detail.basicInfo.vehicleModel,
      vehicleMileage: detail.basicInfo.vehicleMileage,
      serviceAgentId: detail.basicInfo.serviceAgentId,
      serviceAgentName: detail.basicInfo.serviceAgentName,
      serviceCount: taskManager.extraInfo$.value?.serviceRecordCount ?? 0,
      checkTitle: item.title,
      checkTime: item.timestamp,
      checkAuthorId: item.authorId ?? profile.user.memberId,
      checkAuthorName: item.authorName ?? profile.user.name,
      responsiblePersonId: lastRecord?.responsiblePersonId,
      responsiblePersonName: lastRecord?.responsiblePersonName,
      remarks: lastRecord?.remarks,
      medias: lastRecord?.medias ?? [],
    };
    if (isMobile) {
      setActiveReworkRecord(record);
    } else {
      navigation.push('_reworkRecord', {
        recordInfo: record,
        onDone: onFinishEditItemReworkRecord,
      });
    }
  });

  const onRemoveItemReworkRecord = usePersistFn((key: string) => {
    void confirm({
      title: '是否确认删除该内返记录?',
      confirmButtonText: '确认',
      cancelButtonText: '取消',
    }).then(res => {
      if (res) {
        manager.produceItem(key, draft => {
          draft.reworkRecords = [];
        });
      }
    });
  });

  const onCancelEditItemRewordRecord = useCallback(() => {
    setActiveReworkRecord(undefined);
  }, []);

  const onFinishEditItemReworkRecord = usePersistFn(
    (record: ReworkRecordStaged) => {
      manager.produceItem(record._key, draft => {
        const reworkRecords = draft.reworkRecords ?? [];
        if (reworkRecords.length > 0) {
          reworkRecords[0] = record;
        } else {
          reworkRecords.push(record);
        }
        draft.reworkRecords = reworkRecords;
      });
      if (isMobile) {
        setActiveReworkRecord(undefined);
      }
    },
  );

  const onEditUnacceptedReason = usePersistFn((key: string) => {
    presentUnacceptedReasonsActionSheet(unacceptedReason => {
      manager.produceItem(key, (draft, original) => {
        if (original.selectedOptionIndex == null) return;
        draft.unacceptedReason = unacceptedReason;
        changed$.next(key);
      });
    });
  });

  const onOptionCheckChange = usePersistFn(
    (key: string, option: DeliveryCheckItemStagedOption, index: number) => {
      const doUpdate = (unacceptedReason?: string) => {
        manager.produceItem(key, (draft, original) => {
          const expectedIndexSet = new Set(
            original.options
              .map((x, i) => (x.isExpected ? i : undefined))
              .filter(isNotNull),
          );
          const previouslySelected =
            draft.selectedOptionIndex != null
              ? draft.options[draft.selectedOptionIndex]
              : undefined;
          draft.selectedOptionIndex = index;
          draft.resultCode =
            option.isExpected || expectedIndexSet.has(index) || index === 0
              ? 'OK'
              : 'NOT_ACCEPTED';
          draft.resultText = draft.options[index].title;
          draft.defaultRemark = draft.options[index].remark;
          if (draft.remark === previouslySelected?.remark) {
            draft.remark = undefined;
          }
          draft.unacceptedReason = !option.isExpected
            ? unacceptedReason
            : undefined;
          changed$.next(key);
        });
      };

      if (
        !option.isExpected &&
        storeConfiguration.requireDeliveryCheckUnacceptedReason
      ) {
        presentUnacceptedReasonsActionSheet(doUpdate);
      } else {
        doUpdate();
      }
    },
  );

  useEffect(() => {
    const sub = changed$.subscribe(key => {
      const item = staged.items.find(x => x._key === key);
      if (
        errorItemKey === key &&
        (item == null ||
          validateDeliveryCheckItem(manager.sceneType, item) == null)
      ) {
        setErrorItemKey(undefined);
      }
    });
    return () => sub.unsubscribe();
  }, [changed$, errorItemKey, manager.sceneType, staged.items]);

  const onRemove = useCallback(
    async (key: string) => {
      const confirmed = await withConfirmation({
        title: '确认',
        message: '是否确认移除该项?',
        confirmButtonText: '确认',
        cancelButtonText: '取消',
      });
      if (confirmed) {
        manager.produce((draft, original) => {
          const index = original.items.findIndex(x => x._key === key);
          if (index < 0) return;
          draft.items.splice(index, 1);
          draft.currentIndex = Math.min(index, draft.items.length - 1);
        });
      }
    },
    [manager],
  );

  const onSave = usePersistFn(async () => {
    await appLoading.showWithAction(
      async () => {
        await manager.save({ saveOnly: true });
      },
      { delay: 0 },
    );
  });

  const [newItemsScheduled, setNewItemsScheduled] =
    useState<{ key: string; index: number }[]>();

  const notifyNewItems = usePersistFn(
    (newKeys: { key: string; index: number }[]) => {
      delayed(() => {
        onNewWorkItems(newKeys);
      }, 300);
    },
  );

  useEffect(() => {
    if (!newItemsScheduled) return;
    notifyNewItems(newItemsScheduled);
    setNewItemsScheduled(undefined);
  }, [newItemsScheduled, notifyNewItems]);

  const viewReport = useViewReportWithTaskContext();

  const previewReport = usePersistFn(() => {
    const finishButtonText = profile.store.isIndependentDeliveryCheckEnabled
      ? manager.sceneType === DeliveryCheckSceneType.CompletionCheck
        ? '完成竣工检验'
        : detail.basicInfo.status === InspectionTaskStatus.Finished
        ? '完成检验'
        : '允许交车'
      : manager.sceneType === DeliveryCheckSceneType.CompletionCheck
      ? detail.basicInfo.isMainOrder
        ? '继续完工交车'
        : '完成子任务'
      : detail.basicInfo.status === InspectionTaskStatus.Finished
      ? '抽检合格'
      : '允许交车';
    viewReport(
      manager.sceneType === DeliveryCheckSceneType.CompletionCheck
        ? 'deliveryCheckReport'
        : 'samplingCheckReport',
      {
        finishButtonText,
        primaryButton:
          profile.store.isIndependentDeliveryCheckEnabled ||
          manager.sceneType === DeliveryCheckSceneType.SamplingCheck ||
          !detail.basicInfo.isMainTask
            ? undefined
            : {
                text: '继续完工交车',
                onPress: async () => {
                  await appLoading.showWithAction(async () => {
                    const preparedData =
                      await taskManager.getFinishPreparedData();
                    navigation.push('_finish', {
                      timeoutInfo,
                      preparedData,
                    });
                  });
                },
              },
        secondaryButton:
          hasFinishPermission &&
          manager.sceneType === DeliveryCheckSceneType.SamplingCheck &&
          detail.deliveryCheck.status === CommonTaskStatus.Finished &&
          (detail.basicInfo.status !== InspectionTaskStatus.Finished ||
            storeConfiguration.allowSamplingCheckAfterFinish)
            ? {
                text:
                  detail.basicInfo.status === InspectionTaskStatus.Finished
                    ? '抽检不合格'
                    : '禁止交车',
                variant: 'danger',
                bordered: true,
                style: { marginRight: rpx(6) },
                onPress: async context => {
                  await context.finish({
                    resultType: SamplingCheckResultType.Failed,
                  });
                },
              }
            : undefined,
        accessoryText: `${
          manager.sceneType === DeliveryCheckSceneType.CompletionCheck
            ? '质检员'
            : '抽检员'
        }: ${staged.technicianName}`,
        canFinish: () => {
          return hasFinishPermission;
        },
        getAdditionalFinishParams: () => {
          return {
            isSamplingCheckAfterFinish:
              detail.basicInfo.isMainOrder &&
              manager.sceneType === DeliveryCheckSceneType.SamplingCheck,
          };
        },
        onFinish: async (additionalParams?: any) => {
          if (kSimulateCommit) return;
          if (profile.store.isIndependentDeliveryCheckEnabled) {
            if (manager.sceneType === DeliveryCheckSceneType.CompletionCheck) {
              const completionCheckManager = manager as DeliveryCheckManager;
              await completionCheckManager.finish();
            } else {
              const samplingCheckManager = manager as SamplingCheckManager;
              await samplingCheckManager.finish({
                resultType:
                  additionalParams?.resultType ??
                  SamplingCheckResultType.Passed,
              });
            }
          } else if (
            manager.sceneType === DeliveryCheckSceneType.CompletionCheck
          ) {
            await appLoading.showWithAction(async () => {
              await taskManager.finish({
                finishDeliveryCheckWhenNecessary: true,
                timeoutInfo: timeoutInfo?.reason
                  ? {
                      timeoutReason: timeoutInfo?.reason,
                      timeoutRemarks: timeoutInfo?.remark,
                    }
                  : undefined,
              });
            });
          } else if (
            manager.sceneType === DeliveryCheckSceneType.SamplingCheck
          ) {
            const samplingCheckManager = manager as SamplingCheckManager;
            await samplingCheckManager.finish({
              resultType:
                additionalParams?.resultType ?? SamplingCheckResultType.Passed,
            });
          }
        },
        onFinishSuccess: ({ onShare, additionalParams }) => {
          navigation.replace('_hooray', {
            type: 'success',
            soundType: 'chirp',
            buttonText: profile.store.isIndependentDeliveryCheckEnabled
              ? '返回任务首页'
              : '完成',
            onPress: () => {
              if (
                detail.basicInfo.isMainOrder &&
                manager.sceneType === DeliveryCheckSceneType.SamplingCheck &&
                additionalParams?.isSamplingCheckAfterFinish
              ) {
                navigateToTaskDetail();
                if (from === 'service-record') {
                  setTimeout(() => {
                    navigation.goBack();
                  }, 200);
                }
              } else if (
                profile.store.isIndependentDeliveryCheckEnabled ||
                !detail.basicInfo.isMainOrder
              ) {
                navigateToTaskDetail();
              } else {
                event$.next({
                  type: 'finished',
                  orderNo: detail.basicInfo.orderNo,
                  taskNo: detail.basicInfo.taskNo,
                  isMainOrder: detail.basicInfo.isMainOrder,
                });
                navigation.navigate('Main', {
                  screen: 'Tabs',
                  params: { screen: 'History' },
                } as any);
                trackAppReview();
              }
            },
            onShare:
              manager.sceneType === DeliveryCheckSceneType.CompletionCheck
                ? onShare
                : undefined,
          });
        },
        onFinishError: async error => {
          const err = error as HttpError;
          if (err.subCode === 'finish_delivery_checks_with_pending_items') {
            const pendingWorkItems = err.extra.pendingWorkItems as string[];
            const message = [
              `对不起，系统检测到有${pendingWorkItems.length}个项目尚未进行质检，`,
              `分别是: ${pendingWorkItems.join('、')}。\n`,
              '这有可能是在质检开始以后，您的同事增加了施工反馈项目。\n',
              '请完成对这些项目的质检后重试，点击“好”以继续！',
            ].join('');
            await alert({ title: '交车失败', message, okButtonText: '好' });
            void InteractionManager.runAfterInteractions(async () => {
              const newKeys = await appLoading.showWithAction(async () => {
                return await manager.syncWorkItems();
              });
              setNewItemsScheduled(newKeys);
            });
            navigation.goBack();
          } else {
            await alert({ title: '交车失败', message: error.message });
          }
          return true;
        },
      },
    );
  });

  const onConfirm = usePersistFn(async () => {
    let hasInvalidItems = false;

    for (let index = 0; index < staged.items.length; index++) {
      const item = staged.items[index];
      const validateError = validateDeliveryCheckItem(manager.sceneType, item);

      if (validateError != null) {
        hasInvalidItems = true;

        if (item._key !== errorItemKey) {
          setErrorItemKey(item._key);
        }

        onInvalidItem({
          item,
          index,
          error: validateError.type,
          message: validateError.message,
        });

        break;
      }
    }

    if (!hasInvalidItems) {
      if (errorItemKey) {
        setErrorItemKey(undefined);
      }

      if (dirty) {
        await appLoading.showWithAction(
          async () => {
            if (!kSimulateCommit) {
              await manager.save({ saveOnly: false });
            }
            previewReport();
          },
          { delay: 0 },
        );
      } else {
        previewReport();
      }
    }
  });

  useEffect(() => {
    void taskManager.sync();
  }, [taskManager]);

  return {
    pageTitle,
    errorItemKey,
    staged,
    manager,
    sceneType: manager.sceneType,
    taskManager,
    changed$,
    activeReworkRecord,
    onEditUnacceptedReason,
    onRemarkTextChange,
    onOptionCheckChange,
    onRemove,
    onSave,
    onConfirm,
    onViewReworkRecords,
    onEditItemReworkRecord,
    onRemoveItemReworkRecord,
    onCancelEditItemRewordRecord,
    onFinishEditItemReworkRecord,
  };
};
